import { useEffect, useRef, useState, useCallback } from 'react';
import { orgSearch, getOrgByID } from '../../util/util';
import Meta from '../Meta';
import { requireAuth, useAuth } from '../../util/auth';
import UnauthorizedPage from '../../pages/403';
import VenueSection from './VenueSection';
import ShowsSection from './ShowsSection';
import { UserType } from '../../util/data-factory';
import { DataContent } from './DataSection';
import ActivitySection from './ActivitySection';
import OrgCreateModal from './modals/OrgCreateModal';
import OrgBioEditModal from './modals/OrgBioEditModal';
import OrgSearchPicker from '../OrgSearchPicker';
import { IconTooltip } from './ui/TooltipCard';
import IconActionButton from './ui/IconActionButton';
import OnboardingTour from './OnboardingTour';
import DashboardTabDescription from './DashboardTabDescription';
import { PencilSquareIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

function TabButton({ tab, activeTab, onSelect, tooltip, children }) {
  const isActive = tab === activeTab;
  return (
    <IconTooltip label={isActive ? null : tooltip} position="bottom-right">
      <button
        data-tour={`tab-${tab}`}
        className={`text-2xl font-semibold tracking-wide uppercase cursor-pointer select-none transition-colors border-b-2 focus:outline-none ${
          isActive
            ? 'text-slate-700 border-secondary-red'
            : 'text-slate-700/30 border-transparent hover:text-slate-700/50'
        }`}
        onClick={() => onSelect(tab)}
      >
        {children}
      </button>
    </IconTooltip>
  );
}

function DashboardSection() {
  const auth = useAuth();
  const [orgQuery, setOrgQuery] = useState('');
  const [orgs, setOrgs] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [isOrgBioModalOpen, setIsOrgBioModalOpen] = useState(false);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [showsRefreshKey, setShowsRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('manage');
  const [isOrgCreateModalOpen, setIsOrgCreateModalOpen] = useState(false);
  const [tourTargetShowId, setTourTargetShowId] = useState(null);

  const showsSectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const prevSelectedVenueIdRef = useRef(null);
  const orgSelectedInteractivelyRef = useRef(false);
  const venueSectionApiRef = useRef(null);
  const showsSectionApiRef = useRef(null);
  const dataSectionApiRef = useRef(null);

  const [navHeight, setNavHeight] = useState(0);
  useEffect(() => {
    const header = document.querySelector('header');
    if (!header) return;
    const update = () => setNavHeight(header.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);

    header.style.position = 'fixed';
    return () => {
      observer.disconnect();
      header.style.position = '';
    };
  }, []);

  const isAdmin = auth.user.user_type === UserType.ADMIN;
  const isSuperadmin = auth.user.user_type === UserType.SUPERADMIN;
  const effectiveOrgId = isAdmin ? auth.user.org_id : selectedOrgId;

  // Reset venue selection when the active org changes
  useEffect(() => {
    setSelectedVenueId(null);
    prevSelectedVenueIdRef.current = null;
  }, [effectiveOrgId]);

  // Scroll to shows section only on first venue selection, not when switching
  useEffect(() => {
    if (selectedVenueId && !prevSelectedVenueIdRef.current && showsSectionRef.current) {
      showsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevSelectedVenueIdRef.current = selectedVenueId;
  }, [selectedVenueId]);

  const handleShowsNeedRefresh = useCallback(() => {
    setShowsRefreshKey(k => k + 1);
  }, []);

  const handleVenuesAutoScrolled = useCallback(() => {
    orgSelectedInteractivelyRef.current = false;
  }, []);

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleOrgSelected = useCallback(org => {
    orgSelectedInteractivelyRef.current = true;
    setSelectedOrgId(org.org_id);
    setOrgName(org.org_name);
    setOrgInfo(org);
    setOrgs(null);
    setOrgQuery('');
  }, []);

  const searchOrgs = useCallback(async () => {
    try {
      const results = await orgSearch(orgQuery);
      if (results) {
        setOrgs(results);
        setSelectedOrgId(null);
      }
    } catch (error) {
      console.error(error);
    }
  }, [orgQuery]);

  // ADMIN: fetch the org name on mount
  useEffect(() => {
    if (!isAdmin || !auth.user.org_id) return;
    (async () => {
      try {
        const org = await getOrgByID(auth.user.org_id);
        if (org) {
          setOrgName(org.org_name);
          setOrgInfo(org);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [isAdmin, auth.user.org_id]);

  if (!auth.user.user_type) {
    return <UnauthorizedPage />;
  }

  return (
    <>
      <Meta title="Dashboard" />

      <div ref={scrollContainerRef} className="overflow-y-auto" style={{ position: 'fixed', top: navHeight, left: 0, right: 0, bottom: 0 }}>
      {/* ── Page header ─────────────────────────────────────────────── */}
      <section className="w-full bg-white px-4">
        <div className="mx-8 pt-8 pb-6">

          {/* Superadmin: org picker before a gallery is selected */}
          {isSuperadmin && !selectedOrgId && (
            <div className="flex items-center gap-2">
              <OrgSearchPicker
                orgQuery={orgQuery}
                setOrgQuery={setOrgQuery}
                orgs={orgs}
                onSearch={searchOrgs}
                clearOrgs={() => setOrgs(null)}
                onSelect={handleOrgSelected}
              />
              <button
                className="btn btn-outline btn-sm focus:outline-none"
                onClick={() => setIsOrgCreateModalOpen(true)}
              >
                + Add Organization
              </button>
            </div>
          )}

          <OrgCreateModal
            isModalOpen={isOrgCreateModalOpen}
            setIsModalOpen={setIsOrgCreateModalOpen}
            onOrgCreated={handleOrgSelected}
          />

          <OrgBioEditModal
            isModalOpen={isOrgBioModalOpen}
            setIsModalOpen={setIsOrgBioModalOpen}
            orgId={effectiveOrgId}
            org={orgInfo}
            onSaved={newInfo => setOrgInfo(prev => ({ ...prev, ...newInfo }))}
          />

          {/* Gallery name — large title */}
          {orgName && (
            <>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <h1 className="text-4xl text-secondary-red font-semibold tracking-wide uppercase">
                    {orgName}
                  </h1>
                  <IconActionButton
                    label="Edit organization info"
                    icon={PencilSquareIcon}
                    tooltipPosition="bottom"
                    className=""
                    data-tour="edit-bio-button"
                    onClick={() => setIsOrgBioModalOpen(true)}
                  />
                  {isSuperadmin && (
                    <button
                      className="text-xs text-slate-400 hover:text-slate-600 underline focus:outline-none"
                      onClick={() => {
                        setSelectedOrgId(null);
                        setOrgName(null);
                        setOrgInfo(null);
                        setOrgs(null);
                        setActiveTab('manage');
                      }}
                    >
                      Change
                    </button>
                  )}
                </div>
                <OnboardingTour
                  isAdmin={isAdmin}
                  isSuperadmin={isSuperadmin}
                  effectiveOrgId={effectiveOrgId}
                  orgName={orgName}
                  hasSeenOnboarding={auth.user.has_seen_onboarding}
                  updateProfile={auth.updateProfile}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  setIsOrgBioModalOpen={setIsOrgBioModalOpen}
                  setSelectedVenueId={setSelectedVenueId}
                  setTourTargetShowId={setTourTargetShowId}
                  venueSectionApiRef={venueSectionApiRef}
                  showsSectionApiRef={showsSectionApiRef}
                  dataSectionApiRef={dataSectionApiRef}
                />
              </div>

              {/* Section switcher */}
              <div className="flex items-baseline gap-4 mb-6">
                <TabButton
                  tab="manage"
                  activeTab={activeTab}
                  onSelect={setActiveTab}
                  tooltip="Switch to manage venues and shows"
                >
                  Manage
                </TabButton>
                <span className="text-2xl font-light text-slate-700/30">|</span>
                <TabButton
                  tab="analytics"
                  activeTab={activeTab}
                  onSelect={setActiveTab}
                  tooltip="Switch to view engagement analytics"
                >
                  Analytics
                </TabButton>
                {isSuperadmin && (
                  <>
                    <span className="text-2xl font-light text-slate-700/30">|</span>
                    <TabButton
                      tab="activity"
                      activeTab={activeTab}
                      onSelect={setActiveTab}
                      tooltip="Switch to see who changed what for this organization"
                    >
                      Activity
                    </TabButton>
                  </>
                )}
              </div>

              <DashboardTabDescription activeTab={activeTab} isSuperadmin={isSuperadmin} />
            </>
          )}
        </div>
      </section>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <section className="w-full min-h-[calc(100vh-190px)] px-4 pt-0 pb-4 bg-white">
        {effectiveOrgId && activeTab === 'manage' && (
          <VenueSection
            key={effectiveOrgId}
            ref={venueSectionApiRef}
            orgId={effectiveOrgId}
            selectedVenueId={selectedVenueId}
            onSelectVenue={setSelectedVenueId}
            onShowsNeedRefresh={handleShowsNeedRefresh}
            autoScroll={orgSelectedInteractivelyRef.current}
            onAutoScrolled={handleVenuesAutoScrolled}
            isSuperadmin={isSuperadmin}
          />
        )}

        {effectiveOrgId && activeTab === 'manage' && selectedVenueId && (
          <div className="mx-8 min-h-[60vh] pb-8" ref={showsSectionRef}>
            <ShowsSection
              ref={showsSectionApiRef}
              selectedVenueId={selectedVenueId}
              refreshKey={showsRefreshKey}
              tourTargetShowId={tourTargetShowId}
            />
          </div>
        )}

        {effectiveOrgId && activeTab === 'analytics' && (
          <DataContent ref={dataSectionApiRef} effectiveOrgId={effectiveOrgId} />
        )}

        {effectiveOrgId && isSuperadmin && activeTab === 'activity' && (
          <ActivitySection effectiveOrgId={effectiveOrgId} />
        )}
      </section>

      <footer className="footer items-center mt-2 bg-slate-100 px-4 py-1.5 text-xs">
        <aside className="grid-flow-col items-center">
          <p>© {new Date().getFullYear()} Showrunner - All rights reserved</p>
        </aside>
        <nav className="grid-flow-col gap-2 items-center md:place-self-center md:justify-self-end">
          <a href="mailto:admin@showrunner.co" className="text-secondary-red">
            Contact us: admin@showrunner.co
          </a>
          <button
            type="button"
            onClick={scrollToTop}
            aria-label="Scroll to top"
            className="text-secondary-red opacity-40 hover:opacity-100 transition-opacity"
          >
            <ChevronUpIcon className="h-4 w-4" />
          </button>
        </nav>
      </footer>
      </div>
    </>
  );
}

export default requireAuth(DashboardSection, '/admin/login');
